import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  TrendingUp,
  Euro,
  Package,
  MessageSquare,
  Clock,
  Send,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Activity
} from 'lucide-react';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function CrafterAnalytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Data states
  const [revenueData, setRevenueData] = useState([]);
  const [productStats, setProductStats] = useState({ total: 0, totalStock: 0, onCommission: 0, unavailable: 0 });
  const [performanceData, setPerformanceData] = useState({
    messagesReceived: 0,
    messagesReplied: 0,
    avgResponseTime: 0,
    avgOrderTime: 0,
    proposalsSent: 0,
    proposalsAccepted: 0,
    proposalsMonthly: []
  });

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, selectedYear]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load orders for revenue data
      const startOfYear = new Date(selectedYear, 0, 1).toISOString();
      const endOfYear = new Date(selectedYear, 11, 31, 23, 59, 59).toISOString();

      const { data: orders } = await supabase
        .from('orders')
        .select('total, created_at, status')
        .eq('crafter_id', user.id)
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear)
        .in('status', ['confirmed', 'processing', 'shipped', 'delivered', 'completed']);

      // Calculate monthly revenue
      const monthlyRevenue = MONTHS.map((month, index) => {
        const monthOrders = (orders || []).filter(order => {
          const orderMonth = new Date(order.created_at).getMonth();
          return orderMonth === index;
        });
        const revenue = monthOrders.reduce((sum, order) => sum + (order.total || 0), 0);
        const orderCount = monthOrders.length;
        return { month, revenue, orders: orderCount };
      });
      setRevenueData(monthlyRevenue);

      // Load products for inventory stats
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id, customization_options')
        .eq('crafter_id', user.id);

      if (productsError) {
        console.error('Error loading products:', productsError);
      }

      // Calculate product stats based on variant_settings structure
      let totalProducts = (products || []).length;
      let totalStock = 0;
      let onCommissionCount = 0;
      let unavailableCount = 0;

      (products || []).forEach(product => {
        const variantSettings = product.customization_options?.variantSettings || {};
        const inventory = product.customization_options?.inventory || {};
        const defaultTimeline = product.customization_options?.timeline || 'immediate';
        
        // Check all variants
        const variantKeys = Object.keys(variantSettings).length > 0 
          ? Object.keys(variantSettings) 
          : ['default'];
        
        let productHasStock = false;
        let productIsOnCommission = false;
        
        variantKeys.forEach(key => {
          const settings = variantSettings[key] || { timeline: defaultTimeline, stock: inventory[key] || 0 };
          const timeline = settings.timeline || defaultTimeline;
          const stock = settings.stock ?? inventory[key] ?? 0;
          
          if (timeline === 'immediate') {
            totalStock += stock;
            if (stock > 0) productHasStock = true;
          } else {
            productIsOnCommission = true;
          }
        });
        
        if (productIsOnCommission && !productHasStock) {
          onCommissionCount++;
        } else if (!productHasStock && !productIsOnCommission) {
          unavailableCount++;
        }
      });

      const productStatsCalc = {
        total: totalProducts,
        totalStock: totalStock,
        onCommission: onCommissionCount,
        unavailable: unavailableCount
      };
      setProductStats(productStatsCalc);

      // Load messages for performance metrics
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id, created_at')
        .or(`participant_1.eq.${user.id},participant_2.eq.${user.id}`);

      const conversationIds = (conversations || []).map(c => c.id);
      
      let messagesReceived = 0;
      let messagesReplied = 0;
      let totalResponseTime = 0;
      let responseCount = 0;

      if (conversationIds.length > 0) {
        const { data: messages } = await supabase
          .from('messages')
          .select('sender_id, created_at, conversation_id')
          .in('conversation_id', conversationIds)
          .order('created_at', { ascending: true });

        // Calculate message stats
        const messagesByConversation = {};
        (messages || []).forEach(msg => {
          if (!messagesByConversation[msg.conversation_id]) {
            messagesByConversation[msg.conversation_id] = [];
          }
          messagesByConversation[msg.conversation_id].push(msg);
        });

        Object.values(messagesByConversation).forEach(convMessages => {
          let lastClientMessage = null;
          convMessages.forEach(msg => {
            if (msg.sender_id !== user.id) {
              messagesReceived++;
              lastClientMessage = msg;
            } else {
              messagesReplied++;
              if (lastClientMessage) {
                const responseTime = new Date(msg.created_at) - new Date(lastClientMessage.created_at);
                totalResponseTime += responseTime;
                responseCount++;
                lastClientMessage = null;
              }
            }
          });
        });
      }

      // Calculate average response time in hours
      const avgResponseTime = responseCount > 0 
        ? Math.round((totalResponseTime / responseCount) / (1000 * 60 * 60)) 
        : 0;

      // Calculate average order fulfillment time (from order creation to shipped status)
      const { data: shippedOrders } = await supabase
        .from('orders')
        .select('created_at, shipped_at, status')
        .eq('crafter_id', user.id)
        .not('shipped_at', 'is', null);

      let totalOrderTime = 0;
      let validOrderCount = 0;
      (shippedOrders || []).forEach(order => {
        if (order.shipped_at) {
          const orderTime = new Date(order.shipped_at) - new Date(order.created_at);
          if (orderTime > 0) {
            totalOrderTime += orderTime;
            validOrderCount++;
          }
        }
      });
      const avgOrderTime = validOrderCount > 0
        ? Math.round((totalOrderTime / validOrderCount) / (1000 * 60 * 60 * 24))
        : 0;

      // Load proposals with their associated request to determine actual status
      const { data: proposals } = await supabase
        .from('proposals')
        .select('id, status, created_at, request_id, direct_requests(selected_proposal_id)')
        .eq('crafter_id', user.id)
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);

      // Determine actual status for each proposal
      const proposalsWithStatus = (proposals || []).map(proposal => {
        let actualStatus = proposal.status;
        // If proposal is still pending, check if request has selected this proposal
        if (proposal.status === 'pending' && proposal.direct_requests?.selected_proposal_id) {
          if (proposal.direct_requests.selected_proposal_id === proposal.id) {
            actualStatus = 'accepted';
          } else {
            actualStatus = 'rejected';
          }
        }
        return { ...proposal, actualStatus };
      });

      const proposalsSent = proposalsWithStatus.length;
      const proposalsAccepted = proposalsWithStatus.filter(p => p.actualStatus === 'accepted').length;

      // Calculate monthly proposals
      const proposalsMonthly = MONTHS.map((month, index) => {
        const monthProposals = proposalsWithStatus.filter(p => {
          const proposalMonth = new Date(p.created_at).getMonth();
          return proposalMonth === index;
        });
        return { 
          month, 
          sent: monthProposals.length,
          accepted: monthProposals.filter(p => p.actualStatus === 'accepted').length
        };
      });

      setPerformanceData({
        messagesReceived,
        messagesReplied,
        avgResponseTime,
        avgOrderTime,
        proposalsSent,
        proposalsAccepted,
        proposalsMonthly
      });

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = revenueData.reduce((sum, item) => sum + item.revenue, 0);
  const totalOrders = revenueData.reduce((sum, item) => sum + item.orders, 0);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008080]"></div>
        </div>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#008080]" />
          Statistiche e Performance
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedYear(y => y - 1)}
            className="h-8 w-8"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-slate-700 min-w-[60px] text-center">{selectedYear}</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedYear(y => Math.min(y + 1, new Date().getFullYear()))}
            disabled={selectedYear >= new Date().getFullYear()}
            className="h-8 w-8"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Revenue Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#008080]/10 flex items-center justify-center">
              <Euro className="w-5 h-5 text-[#008080]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Ricavi {selectedYear}</h3>
              <p className="text-sm text-slate-500">{totalOrders} ordini completati</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-[#008080]">€{totalRevenue.toFixed(2)}</p>
            <p className="text-xs text-slate-500">Totale anno</p>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" tickFormatter={(v) => `€${v}`} />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const revenueValue = payload[0]?.value || 0;
                    const ordersValue = payload[0]?.payload?.orders || 0;
                    return (
                      <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-lg">
                        <p className="font-semibold text-slate-700 mb-2">{label}</p>
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Numero di ordini:</span> {ordersValue}
                        </p>
                        <p className="text-sm text-[#008080]">
                          <span className="font-medium">Ricavi:</span> €{revenueValue.toFixed(2)}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="revenue" fill="#008080" radius={[4, 4, 0, 0]} name="Ricavi" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Products & Performance Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Products Inventory */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#DAA520]/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-[#DAA520]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Inventario Prodotti</h3>
              <p className="text-sm text-slate-500">Panoramica del tuo catalogo</p>
            </div>
          </div>
          
          {productStats.total > 0 ? (
            <div className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#008080]">{productStats.total}</p>
                  <p className="text-xs text-slate-500">Prodotti a catalogo</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#DAA520]">{productStats.totalStock}</p>
                  <p className="text-xs text-slate-500">Unità a magazzino</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{productStats.onCommission}</p>
                  <p className="text-xs text-slate-500">Su commissione</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-500">{productStats.unavailable}</p>
                  <p className="text-xs text-slate-500">Non disponibili</p>
                </div>
              </div>

              {/* Visual Bar */}
              {(productStats.totalStock > 0 || productStats.onCommission > 0) && (
                <div className="pt-2">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                    <span>Distribuzione catalogo</span>
                  </div>
                  <div className="flex h-3 rounded-full overflow-hidden bg-slate-200">
                    {productStats.totalStock > 0 && (
                      <div 
                        className="bg-[#008080] transition-all"
                        style={{ width: `${(productStats.total - productStats.onCommission - productStats.unavailable) / productStats.total * 100}%` }}
                        title="Disponibili"
                      />
                    )}
                    {productStats.onCommission > 0 && (
                      <div 
                        className="bg-purple-500 transition-all"
                        style={{ width: `${productStats.onCommission / productStats.total * 100}%` }}
                        title="Su commissione"
                      />
                    )}
                    {productStats.unavailable > 0 && (
                      <div 
                        className="bg-red-400 transition-all"
                        style={{ width: `${productStats.unavailable / productStats.total * 100}%` }}
                        title="Non disponibili"
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-[#008080]" />
                      <span className="text-xs text-slate-500">Disponibili</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-purple-500" />
                      <span className="text-xs text-slate-500">Commissione</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <span className="text-xs text-slate-500">Esauriti</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nessun prodotto nel catalogo</p>
            </div>
          )}
        </Card>

        {/* Performance Metrics */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Performance</h3>
              <p className="text-sm text-slate-500">Interazioni con i clienti</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <MessageSquare className="w-6 h-6 text-[#008080] mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{performanceData.messagesReceived}</p>
              <p className="text-xs text-slate-500">Messaggi ricevuti</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <Clock className="w-6 h-6 text-[#DAA520] mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{performanceData.avgResponseTime}h</p>
              <p className="text-xs text-slate-500">Tempo medio risposta messaggi</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <Send className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{performanceData.proposalsSent}</p>
              <p className="text-xs text-slate-500">Proposte inviate</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{performanceData.avgOrderTime}gg</p>
              <p className="text-xs text-slate-500">Tempo medio evasione ordine</p>
            </div>
          </div>

          {performanceData.proposalsSent > 0 && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600">Tasso di conversione proposte</span>
                <span className="font-semibold text-green-600">
                  {Math.round((performanceData.proposalsAccepted / performanceData.proposalsSent) * 100)}%
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${(performanceData.proposalsAccepted / performanceData.proposalsSent) * 100}%` }}
                />
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Proposals Trend */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Send className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Andamento Proposte {selectedYear}</h3>
            <p className="text-sm text-slate-500">Proposte inviate vs accettate</p>
          </div>
        </div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={performanceData.proposalsMonthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="sent" 
                stroke="#008080" 
                strokeWidth={2}
                dot={{ fill: '#008080', strokeWidth: 2 }}
                name="Inviate"
              />
              <Line 
                type="monotone" 
                dataKey="accepted" 
                stroke="#10B981" 
                strokeWidth={2}
                dot={{ fill: '#10B981', strokeWidth: 2 }}
                name="Accettate"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </motion.div>
  );
}
